import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getActiveSymbols } from '../api/symbolApi';
import { useAuth } from './authProvider';

/**
 * SymbolProvider - Centralized Symbol State Management
 *
 * Provides global symbol state across all components.
 * Fetches active symbols ONCE on login, persists to localStorage.
 *
 * Usage:
 *   import { useSymbols } from '../provider/symbolProvider';
 *   const { symbols, symbolNames, loading } = useSymbols();
 *
 * Benefits:
 * - Single API call per session (not per component)
 * - Isolated re-renders (doesn't re-render on tick updates like SignalR)
 * - Persisted to localStorage (survives page refresh)
 * - Clean separation from real-time data
 */

const SymbolContext = createContext();

export const useSymbols = () => {
    const context = useContext(SymbolContext);
    if (!context) {
        throw new Error('useSymbols must be used within SymbolProvider');
    }
    return context;
};

export const SymbolProvider = ({ children }) => {
    const { token } = useAuth();

    // Initialize from localStorage if available
    const [symbols, setSymbols] = useState(() => {
        const cached = localStorage.getItem('activeSymbols');
        if (cached) {
            try {
                return JSON.parse(cached);
            } catch {
                return [];
            }
        }
        return [];
    });

    const [symbolNames, setSymbolNames] = useState(() => {
        const cached = localStorage.getItem('activeSymbolNames');
        if (cached) {
            try {
                return JSON.parse(cached);
            } catch {
                return [];
            }
        }
        return [];
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(() => {
        // Check if we already have cached data
        return localStorage.getItem('activeSymbols') !== null;
    });

    // Fetch symbols when token becomes available (and we haven't fetched yet)
    useEffect(() => {
        if (token && !hasFetched) {
            fetchSymbols();
        }
    }, [token, hasFetched]);

    const fetchSymbols = async () => {
        if (!token) {
            console.log('[SymbolProvider] ⚠️ No token, skipping fetch');
            return;
        }

        try {
            console.log('[SymbolProvider] 🔄 Fetching active symbols...');
            setLoading(true);
            setError(null);

            const response = await getActiveSymbols(token);

            // Handle multiple API response formats
            let symbolList = [];
            if (Array.isArray(response)) {
                symbolList = response;
            } else if (response?.data && Array.isArray(response.data)) {
                symbolList = response.data;
            }

            // Filter for active symbols
            const activeSymbols = symbolList.filter(
                s => s.isActive === 1 || s.isActive === true
            );

            // Extract and sort symbol names
            const names = activeSymbols
                .map(s => s.symbol || s.name || s.symbolName)
                .filter(Boolean)
                .sort();

            console.log('[SymbolProvider] ✅ Loaded', activeSymbols.length, 'active symbols');

            // Update state
            setSymbols(activeSymbols);
            setSymbolNames(names);
            setHasFetched(true);

            // Persist to localStorage
            localStorage.setItem('activeSymbols', JSON.stringify(activeSymbols));
            localStorage.setItem('activeSymbolNames', JSON.stringify(names));

        } catch (err) {
            console.error('[SymbolProvider] ❌ Error fetching symbols:', err);
            setError(err.message || 'Failed to fetch symbols');
        } finally {
            setLoading(false);
        }
    };

    // Manual refetch (useful after adding/removing symbols)
    const refetchSymbols = useCallback(async () => {
        console.log('[SymbolProvider] 🔄 Manual refetch triggered');
        setHasFetched(false);
        await fetchSymbols();
    }, [token]);

    // Clear cached symbols (useful on logout)
    const clearSymbols = useCallback(() => {
        console.log('[SymbolProvider] 🗑️ Clearing symbols cache');
        setSymbols([]);
        setSymbolNames([]);
        setHasFetched(false);
        localStorage.removeItem('activeSymbols');
        localStorage.removeItem('activeSymbolNames');
    }, []);

    // Helper: Find symbol by name
    const getSymbolByName = useCallback((name) => {
        return symbols.find(
            s => (s.symbol || s.name || s.symbolName) === name
        ) || null;
    }, [symbols]);

    // Context value - memoized to prevent unnecessary re-renders
    const value = React.useMemo(() => ({
        // State
        symbols,        // Full symbol objects array
        symbolNames,    // Just names array (for dropdowns)
        loading,
        error,

        // Actions
        refetchSymbols,
        clearSymbols,

        // Helpers
        getSymbolByName,
    }), [symbols, symbolNames, loading, error, refetchSymbols, clearSymbols, getSymbolByName]);

    return (
        <SymbolContext.Provider value={value}>
            {children}
        </SymbolContext.Provider>
    );
};

export default SymbolProvider;
